import { createElement } from 'react';

import {
    type DynamicWidgetAdapter,
    type DynamicWidgetBuildContext,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_registry';
import {
    buildBindingPresentation,
    buildDynamicWidgetId,
    type ResourceShiftListWidgetModel,
    resourceShiftListWidgetModelSchema,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import {
    addRetrieveEnvelopeBindings,
    buildRetrieveArtifact,
    matchesRetrieveDomain,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_artifact_adapter';
import { RetrieveArtifactController } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_artifact_controller';
import { hydrateShiftArtifactModel } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/shift_list_model';
import { ShiftListWidget } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/shift_list_widget';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';

const DOMAIN = 'shifts';

export function buildRetrieveShiftListWidgetModel(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
): ResourceShiftListWidgetModel | null {
    const artifact = buildRetrieveArtifact(entry, context, DOMAIN);
    if (artifact === null || !entry.toolCallEventId || !entry.runId) {
        return null;
    }
    addRetrieveEnvelopeBindings({ artifact });
    for (const [field, paths] of [
        ['shiftId', ['shift_id']],
        ['patientName', ['client/name']],
        ['day', ['start_time']],
        ['timeLabel', ['start_time', 'end_time']],
        ['status', ['status']],
        ['caregiverId', ['assigned_caregiver/id']],
        ['caregiverName', ['assigned_caregiver/name']],
        ['outreachState', ['outreach_state_summary']],
    ] as const) {
        artifact.builder.addCalculatedField({
            id: `preview.rows.${field}`,
            sourceIds: [artifact.eventSourceId],
            paths: paths.map((path) => `/head_preview/*/${path}`),
            transform: 'mapRetrieveShiftPreviewRows',
            rawValue: artifact.envelope.head_preview,
            value: null,
            renderedValue: null,
            presentation: buildBindingPresentation({
                section: 'rows',
                label: field,
                role: field === 'shiftId' ? 'control' : 'field',
            }),
        });
        artifact.builder.addLiveField({
            id: `resource.rows.${field}`,
            sourceIds: [artifact.resourceSourceId],
            paths: paths.map((path) => `/rows/*/${path}`),
            presentation: buildBindingPresentation({
                section: 'rows',
                label: field,
                role: field === 'shiftId' ? 'control' : 'field',
            }),
        });
    }
    const previewCount = artifact.envelope.head_preview.length;
    const receiptModel = resourceShiftListWidgetModelSchema.parse({
        ...artifact.builder.buildBase({
            widgetId: buildDynamicWidgetId(entry.toolCallEventId, 'shifts'),
            templateVersion: 3,
            issues: [],
        }),
        template: 'shifts',
        templateVersion: 3,
        rows: [],
        dayGroups: [],
        note:
            artifact.envelope.row_count === 0
                ? null
                : `Previewing ${previewCount} of ${artifact.envelope.row_count} shifts while the full artifact loads.`,
        rowCount: artifact.envelope.row_count,
        showHeaderCount: true,
        openShiftCount: 0,
        bulkOpenShiftCount: 0,
        bulkShiftIds: [],
        workspaceFile: {
            runId: entry.runId,
            path: artifact.workspacePath,
        },
        rowProvenance: 'event_preview',
    });
    return hydrateShiftArtifactModel(
        receiptModel,
        artifact.envelope.head_preview,
        {
            sourceKind: 'event_source',
            bindingId: 'previewRows',
            issuePrefix: 'preview-row',
            rowProvenance: 'event_preview',
        },
    );
}

export const retrieveShiftListAdapter: DynamicWidgetAdapter<ResourceShiftListWidgetModel> =
    {
        template: 'shifts',
        templateVersion: 3,
        toolNames: new Set(['retrieve', 'retrieve_shifts']),
        matches: (entry) => matchesRetrieveDomain(entry, DOMAIN),
        build: buildRetrieveShiftListWidgetModel,
        render(model, handlers) {
            return createElement(
                RetrieveArtifactController<ResourceShiftListWidgetModel>,
                {
                    model,
                    hydrateRows: (previewModel, rows) => ({
                        ...hydrateShiftArtifactModel(previewModel, rows, {
                            sourceKind: 'resource_source',
                            bindingId: 'resourceRows',
                            issuePrefix: 'workspace-row',
                            rowProvenance: 'resource',
                        }),
                        note: null,
                    }),
                    render: ({
                        model: completeModel,
                        artifactHasLoaded,
                        loadError,
                        hasMoreRows,
                        isLoadingMoreRows,
                        loadAllRows,
                    }) =>
                        createElement(ShiftListWidget, {
                            model:
                                loadError && !artifactHasLoaded
                                    ? {
                                          ...completeModel,
                                          note:
                                              completeModel.rowCount === 0
                                                  ? null
                                                  : 'Preview only — the full shift artifact is unavailable.',
                                      }
                                    : completeModel,
                            onCaregiverClick: handlers.onCaregiverClick,
                            onViewOutreach:
                                handlers.onPromptAction === undefined
                                    ? undefined
                                    : (shiftId) =>
                                          handlers.onPromptAction?.(
                                              `View outreach for [[shift:${shiftId}]]`,
                                          ),
                            totalRowCount: model.rowCount,
                            hasMoreRows: artifactHasLoaded
                                ? hasMoreRows
                                : model.rowCount > model.rows.length,
                            isLoadingMoreRows,
                            onShowAllRows: loadAllRows,
                        }),
                },
            );
        },
    };
